/**
 * Light palette. Night stays the token defaults in `src/theme/index.ts`.
 *
 * The custom properties are the same ones the developer theme switcher writes:
 * overriding them restyles every surface that reads the palette, including
 * semantic tokens that point at the riftbound colors.
 */

export interface Palette {
  bg: string;
  bgRgb: string;
  bgElevated: string;
  surface: string;
  border: string;
  text: string;
  textMuted: string;
  accent: string;
  accentHover: string;
  accentMuted: string;
  violet: string;
  danger: string;
  success: string;
  /** RGB triplet for the play-shell glow. */
  glow: string;
  /** Top stop of the play-shell wash. */
  shellTop: string;
}

/** Parchment and ink, with bronze gold dark enough for light button labels. */
export const LIGHT_PALETTE: Palette = {
  bg: '#F7F3EA',
  bgRgb: '247, 243, 234',
  bgElevated: '#EFE8D8',
  surface: '#E6DCC8',
  border: '#D0C2AA',
  text: '#1A1428',
  /** Dark enough to read on parchment and on the elevated card surface. */
  textMuted: '#463E58',
  accent: '#7A5A16',
  accentHover: '#846318',
  accentMuted: '#5C4614',
  violet: '#5B3FD4',
  danger: '#B42323',
  success: '#0F5C34',
  glow: '122, 90, 22',
  shellTop: '#E8DCC4',
};

export function paletteVars(palette: Palette): Record<string, string> {
  const p = palette;
  return {
    '--chakra-colors-riftbound-bg': p.bg,
    '--chakra-colors-riftbound-bg-elevated': p.bgElevated,
    '--chakra-colors-riftbound-surface': p.surface,
    '--chakra-colors-riftbound-border': p.border,
    '--chakra-colors-riftbound-text': p.text,
    '--chakra-colors-riftbound-text-muted': p.textMuted,
    '--chakra-colors-riftbound-accent': p.accent,
    '--chakra-colors-riftbound-accent-hover': p.accentHover,
    '--chakra-colors-riftbound-accent-muted': p.accentMuted,
    '--chakra-colors-riftbound-violet': p.violet,
    '--chakra-colors-riftbound-danger': p.danger,
    '--chakra-colors-riftbound-success': p.success,
    '--chakra-colors-bg': p.bg,
    '--chakra-colors-bg-subtle': p.bgElevated,
    '--chakra-colors-bg-muted': p.bgElevated,
    '--chakra-colors-bg-emphasized': p.surface,
    '--chakra-colors-bg-inverted': p.text,
    '--chakra-colors-bg-panel': p.surface,
    '--chakra-colors-bg-error': p.danger,
    '--chakra-colors-bg-warning': p.accentMuted,
    '--chakra-colors-bg-success': p.success,
    '--chakra-colors-bg-info': p.violet,
    '--chakra-colors-bg-elevated': p.bgElevated,
    '--chakra-colors-surface': p.surface,
    '--chakra-colors-border': p.border,
    '--chakra-colors-text': p.text,
    '--chakra-colors-text-muted': p.textMuted,
    '--chakra-colors-accent': p.accent,
    '--chakra-colors-accent-hover': p.accentHover,
    '--chakra-colors-accent-muted': p.accentMuted,
    '--chakra-colors-selected-bg': p.accent,
    '--chakra-colors-selected-fg': p.bg,
    '--chakra-colors-brand-gold-contrast': p.bg,
    '--chakra-colors-brand-gold-fg': p.text,
    '--chakra-colors-brand-gold-subtle': p.surface,
    '--chakra-colors-brand-gold-muted': p.border,
    '--chakra-colors-brand-gold-emphasized': p.accent,
    '--chakra-colors-brand-gold-solid': p.accent,
    '--chakra-colors-brand-gold-focus-ring': p.accent,
    '--chakra-colors-brand-gold-border': p.accent,
    '--riftbound-bg-rgb': p.bgRgb,
    '--riftbound-shell-glow': p.glow,
    '--riftbound-shell-top': p.shellTop,
  };
}

export const COLOR_SCHEME_STYLE_ID = 'riftbound-color-scheme';

/**
 * Night keeps a deep lift. Daylight uses a short contact shadow plus a wide,
 * low-opacity one tinted with the ink, which reads as elevation without a gray stain.
 */
export const NIGHT_RAISED_SHADOW = '0 12px 34px rgba(0, 0, 0, 0.18)';
export const LIGHT_RAISED_SHADOW =
  '0 1px 2px rgba(26, 20, 40, 0.04), 0 8px 18px rgba(26, 20, 40, 0.05)';

/** Type on art. The scrim carries contrast in daylight, so the shadow goes away. */
export const NIGHT_TEXT_SHADOW = '0 1px 10px rgba(0, 0, 0, 0.9), 0 1px 2px rgba(0, 0, 0, 0.8)';

/**
 * Day colors apply when the document is `.light` (forced day or a resolved
 * system day), or when the system prefers light and nothing has pinned
 * `.dark`. Night is the token default, so it needs no override.
 *
 * Chakra's light greens (200–400) are foreground colors tuned for night.
 * Daylight shifts them down so small type, like a legal deck count, still reads.
 */
const LIGHT_FOREGROUND_GREENS: Record<string, string> = {
  '--chakra-colors-green-200': '#14663C',
  '--chakra-colors-green-300': '#116038',
  '--chakra-colors-green-400': '#0F5C34',
};

/** Shared with `next-themes` in `main.tsx` and the FOUC bootstrap in `index.html`. */
export const COLOR_SCHEME_STORAGE_KEY = 'riftbound-color-scheme';

export type ColorSchemePreference = 'system' | 'light' | 'dark';

export function lightSchemeCss(): string {
  const declarations = Object.entries({
    ...paletteVars(LIGHT_PALETTE),
    ...LIGHT_FOREGROUND_GREENS,
  })
    .map(([key, value]) => `${key}: ${value};`)
    .join('');
  const dayBlock = [
    'color-scheme: light;',
    declarations,
    `--riftbound-shadow-raised: ${LIGHT_RAISED_SHADOW};`,
    '--riftbound-shadow-text: none;',
  ].join('');
  return [
    'html {',
    'color-scheme: dark;',
    `--riftbound-shadow-raised: ${NIGHT_RAISED_SHADOW};`,
    `--riftbound-shadow-text: ${NIGHT_TEXT_SHADOW};`,
    '}',
    // Forced day from account preference (or next-themes resolved day class).
    'html.light {',
    dayBlock,
    '}',
    // System day when the preference is system and night is not pinned.
    '@media (prefers-color-scheme: light) {',
    'html:not(.dark) {',
    dayBlock,
    '}',
    '}',
  ].join('');
}

/** Paint the scheme before React. Safe to call more than once. */
export function installColorScheme(): void {
  if (typeof document === 'undefined') return;
  let style = document.getElementById(COLOR_SCHEME_STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = COLOR_SCHEME_STYLE_ID;
    document.head.appendChild(style);
  }
  style.textContent = lightSchemeCss();
}
